import { createTheme } from '@mui/material/styles';

// Semantische kleuren en lagen. Gebruik in componenten altijd deze tokens
// (bv. sx={{ bgcolor: 'onPrimary.hover', zIndex: 'overlay' }}) in plaats van
// hex/rgba-waarden; zie docs/CONVENTIONS.md. Kaartkleuren (OpenLayers) staan in
// src/config/mapColors.js.

// Witte tinten op een primaire (blauwe) achtergrond: navbar, kopteksten, sidebar
const onPrimary = {
  text: 'rgba(255, 255, 255, 0.7)',
  subtle: 'rgba(255, 255, 255, 0.05)',
  hover: 'rgba(255, 255, 255, 0.1)',
  active: 'rgba(255, 255, 255, 0.2)',
  divider: 'rgba(255, 255, 255, 0.12)',
  outline: 'rgba(255, 255, 255, 0.5)',
};

// Lagen boven de MUI-standaard (appBar 1100, drawer 1200, modal 1300, snackbar 1400,
// tooltip 1500). overlay/floating/topmost gelden binnen hun eigen stacking context.
const zIndex = {
  overlay: 1000, // laadscherm of paneel over pagina-inhoud
  floating: 2000, // zwevende knoppen en voortgang boven overlays
  topmost: 10000, // volledig scherm binnen een dialoog (bv. intekentool)
};

const commonSettings = {
  zIndex,
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
  },
  components: {
    MuiButton: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiTextField: {
      defaultProps: {
        size: 'small',
      },
    },
    // Sinds x-date-pickers 8 rendert het datumveld een PickersTextField, die
    // MuiTextField-defaults niet meer leest.
    MuiPickersTextField: {
      defaultProps: {
        size: 'small',
      },
    },
    // MUI 9 verlaagde de standaard min-width van 56px naar 36px; behoud de
    // bestaande uitlijning in de navigatie.
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 56,
        },
      },
    },
    // Add other common component defaults here if needed
  },
};

// Light theme based on the original theme
export const lightTheme = createTheme({
  ...commonSettings,
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2', // Original primary color
    },
    secondary: {
      main: '#dc004e', // Original secondary color
    },
    background: {
      default: '#f5f5f5', // Standard light background
      paper: '#ffffff', // Standard light paper background
    },
    text: {
      primary: 'rgba(0, 0, 0, 0.87)', // Standard light text
      secondary: 'rgba(0, 0, 0, 0.6)',
    },
    onPrimary,
    // Documentstatussen (chips, knoppen, legenda)
    status: {
      intake: '#424242',
      to_review: '#1976d2',
      reviewed: '#115293',
      exported: '#4791db',
    },
    // Halfdoorzichtige kaart (login/2FA) en verduisterde achtergrond
    glass: { background: 'rgba(255, 255, 255, 0.85)', shadow: 'rgba(0, 0, 0, 0.15)' },
    scrim: 'rgba(0, 0, 0, 0.5)',
  },
});

// Dark theme definition
export const darkTheme = createTheme({
  ...commonSettings,
  palette: {
    mode: 'dark',
    primary: {
      main: '#90caf9', // Lighter blue for dark mode contrast
    },
    secondary: {
      main: '#f48fb1', // Lighter pink/red for dark mode contrast
    },
    background: {
      default: '#121212', // Standard dark background
      paper: '#1e1e1e', // Standard dark paper background
    },
    text: {
      primary: '#ffffff', // Standard dark text
      secondary: 'rgba(255, 255, 255, 0.7)',
    },
    onPrimary,
    status: {
      intake: '#bdbdbd',
      to_review: '#90caf9',
      reviewed: '#64b5f6',
      exported: '#4791db',
    },
    glass: { background: 'rgba(0, 0, 0, 0.6)', shadow: 'rgba(0, 0, 0, 0.8)' },
    scrim: 'rgba(0, 0, 0, 0.7)',
  },
});

// We no longer export a single default theme
// export default theme;
